import { useState } from 'react'
import { z } from 'zod'
import { useAtom } from 'jotai'
import { currentStep } from '@/app/store'
import { useQuery } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useToast } from '@/view/components/ui/use-toast'
import { Customer, usePanContext } from '@/app/contexts/PanContext'
import { customerService } from '@/app/services/customerService'
import { createCustomerBody } from '@/app/services/customerService/createCustomer'
import { transactionsService } from '@/app/services/transactionService'
import { usePanMutations } from '@/app/hooks/usePanMutations'
import { BasicInput } from '@/view/components/BasicInput'
import BasicOptions from '@/view/components/BasicOptions'
import { BasicSelect } from '@/view/components/BasicSelect'
import { Button } from '@/view/components/ui/button'

export const bankFormSchema = z.object({
  accountType: z
    .string({ required_error: '* obrigatório' })
    .min(1, '* obrigatório'),
  bank: z.string({ required_error: '* obrigatório' }).min(1, '* obrigatório'),
  agency: z.string().min(3, '* obrigatório').max(5, 'No maximo 5 caracteres'),
  account: z.string().min(4, '* obrigatório'),
})

type bankFormData = z.infer<typeof bankFormSchema>

interface BankInfoFormProps {
  visible?: boolean
}
export default function BankInfoForm({ visible }: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep)
  const { customer, setCustomer, proposal, preTransactionId, setTransactionId } = usePanContext()
  const { toast } = useToast()

  const { sendProposalMutation } = usePanMutations()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const bankForm = useForm<bankFormData>({
    resolver: zodResolver(bankFormSchema),
  })

  const {
    handleSubmit,
    formState: { errors },
  } = bankForm

  const { data: banks, isLoading } = useQuery({
    queryKey: ['banks'],
    queryFn: () => transactionsService.banks(),
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }))
    },
  })

  const submitForm = async (data: bankFormData) => {
    if (!customer) return

    setIsSubmitting(true)

    const updatedCustomerData: Customer = {
      ...customer,
      bankAccount: data,
    }

    try {
      await customerService.createCustomer(
        updatedCustomerData as createCustomerBody,
      )

      setCustomer({
        ...updatedCustomerData,
      })

      if (proposal) {
        const transaction = await sendProposalMutation.mutateAsync({
          id: preTransactionId!,
          payload: proposal,
        })
        setTransactionId(transaction._id)
      }

      setStep((state) => state + 1)
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description:
          error?.message || 'Erro inesperado. Tente novamente mais tarde.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={visible ? 'flex flex-col gap-8' : 'hidden'}>
      <div className="grid grid-cols-inputs gap-y-4">
        <FormProvider {...bankForm}>
          <BasicOptions
            options={[
              { label: 'Poupança', value: 'CP' },
              { label: 'Corrente', value: 'CC' },
            ]}
            name="accountType"
            label="Tipo de conta"
            error={errors.accountType?.message}
          />

          <div />

          {!isLoading && (
            <div className="mr-4">
              <BasicSelect
                name="bank"
                label="Banco"
                error={errors.bank?.message}
                options={banks || []}
                placeholder="Selecione seu banco"
                tabIndex={6}
              />
            </div>
          )}

          <div className="mr-8 grid grid-cols-2 gap-4">
            <BasicInput
              tabIndex={0}
              name="agency"
              label="Agencia"
              error={errors.agency?.message}
              type="number"
              maxLength={4}
            />
            <BasicInput
              tabIndex={0}
              name="account"
              label="Conta"
              error={errors.account?.message}
              type="number"
            />
          </div>
        </FormProvider>
      </div>
      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={handleSubmit(submitForm)}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Continuar
        </Button>
      </div>
    </div>
  )
}
